import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, doc, updateDoc, writeBatch, orderBy } from 'firebase/firestore';
import { Container, Card, Form, Button, Spinner, Alert, Table, Tabs, Tab, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';

// 発注番号をハイフン区切りにフォーマットするヘルパー関数
const formatDisplayOrderNumber = (orderNumber) => {
  if (!orderNumber || orderNumber.length < 13) return orderNumber;
  const datePart = orderNumber.substring(0, 8);
  const dailyPart = orderNumber.substring(8, 11);
  const destPart = orderNumber.substring(11);
  return `${datePart}-${dailyPart}-${destPart}`;
};

function ManualShipment() {
  const [orders, setOrders] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [inputData, setInputData] = useState({});
  
  // 【コメントアウト】本日出荷分のみ表示するかの状態管理
  // const [showOnlyToday, setShowOnlyToday] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersDataMap = new Map();
      usersSnapshot.forEach(doc => usersDataMap.set(doc.id, doc.data()));
      setUsersMap(usersDataMap);

      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('status', 'in', ['processed', 'shipped']), orderBy('orderedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedOrders = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(fetchedOrders);

    } catch (err) {
      setError('注文情報の取得に失敗しました。');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = '手入力出荷処理 | My Order System';
    fetchData();
  }, [fetchData]);

  // 【変更点】受付済み注文リストにも本日出荷分のみをフィルタリングするロジックを追加
  const destinationsToProcess = useMemo(() => {
    const allProcessed = orders
      .filter(o => o.status === 'processed')
      .flatMap(order => 
        order.shippingDestinations
          .filter(dest => !dest.trackingNumber)
          .map(dest => ({ ...order, dest }))
      );
    
    const todayStr = new Date().toLocaleDateString('sv'); // 'YYYY-MM-DD' 形式
    return allProcessed.filter(item => item.estimatedShippingDate === todayStr);

  }, [orders]);

  const destinationsShipped = useMemo(() => {
    const allShipped = orders
      .filter(o => o.status === 'shipped')
      .flatMap(order => 
        order.shippingDestinations
          .map(dest => ({ ...order, dest }))
      );
      
    // 【コメントアウト】切り替え機能が不要なため、条件分岐をコメントアウト
    // if (!showOnlyToday) {
    //     return allShipped;
    // }

    const todayStr = new Date().toLocaleDateString('sv'); // 'YYYY-MM-DD' 形式
    return allShipped.filter(item => item.estimatedShippingDate === todayStr);

  }, [orders]);


  const handleInputChange = (orderNumber, field, value) => {
    setInputData(prev => ({
      ...prev,
      [orderNumber]: {
        ...prev[orderNumber],
        [field]: value,
      },
    }));
  };

  const handleUpdate = async () => {
    setError('');
    setSuccess('');
    const updatesToPerform = Object.entries(inputData)
      .filter(([_, data]) => data.trackingNumber || data.carrier);

    if (updatesToPerform.length === 0) {
      setError('更新する情報が入力されていません。');
      return;
    }
    setUpdating(true);

    try {
      const batch = writeBatch(db);
      let updatedCount = 0;

      for (const [orderNumber, changedData] of updatesToPerform) {
        const parentOrder = orders.find(o => o.shippingDestinations.some(d => d.orderNumber === orderNumber));
        if (parentOrder) {
          
          const updatedDestinations = parentOrder.shippingDestinations.map(dest => {
            if (dest.orderNumber === orderNumber) {
              return { 
                ...dest, 
                trackingNumber: changedData.trackingNumber ?? dest.trackingNumber,
              };
            }
            return dest;
          });
          
          const newCarrier = changedData.carrier ?? parentOrder.shippingCarrier;
          const allShipped = updatedDestinations.every(d => d.trackingNumber);
          const newStatus = allShipped ? 'shipped' : 'processed';

          const orderRef = doc(db, 'orders', parentOrder.id);
          batch.update(orderRef, {
            shippingDestinations: updatedDestinations,
            status: newStatus,
            shippingCarrier: newCarrier,
          });
          updatedCount++;
        }
      }
      
      await batch.commit();
      setSuccess(`${updatedCount}件の出荷情報を更新しました。`);
      setInputData({});
      fetchData();

    } catch (err) {
      setError('データベースの更新中にエラーが発生しました。');
      console.error(err);
    }
    setUpdating(false);
  };

  // 【変更点】対象がない場合のメッセージを修正
  const renderShipmentTable = (destList, isShippedTab = false) => {
    if (destList.length === 0) {
      return <p className="text-muted mt-3">{isShippedTab ? "本日出荷予定の出荷済み注文はありません。" : "本日出荷予定の受付済み注文はありません。"}</p>;
    }
    return (
      <Table striped bordered hover responsive className="mt-3">
        <thead>
          <tr>
            <th>発注番号</th>
            <th>送付先</th>
            <th>発注者</th>
            <th>出荷予定日</th>
            <th>送り状番号 (お問い合わせ番号)</th>
            <th>配送会社</th>
          </tr>
        </thead>
        <tbody>
          {destList.map(item => {
            const currentTracking = inputData[item.dest.orderNumber]?.trackingNumber ?? item.dest.trackingNumber ?? '';
            const currentCarrier = inputData[item.dest.orderNumber]?.carrier ?? item.shippingCarrier ?? '';
            return (
              <tr key={item.dest.orderNumber}>
                <td>{formatDisplayOrderNumber(item.dest.orderNumber)}</td>
                <td>{item.dest.recipientName} 様</td>
                <td>{usersMap.get(item.orderedByUid)?.personName || '不明'}</td>
                <td>{item.estimatedShippingDate}</td>
                <td>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={currentTracking}
                    onChange={(e) => handleInputChange(item.dest.orderNumber, 'trackingNumber', e.target.value)}
                    readOnly={isShippedTab}
                  />
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={currentCarrier}
                    onChange={(e) => handleInputChange(item.dest.orderNumber, 'carrier', e.target.value)}
                    disabled={isShippedTab}
                  >
                    <option value="" disabled>選択してください</option>
                    <option value="yupri">ゆうプリR (日本郵便)</option>
                    <option value="istar">福山通運 (iSTAR)</option>
                  </Form.Select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">手入力出荷処理</Card.Header>
          <Card.Body className="p-4">
            <Alert variant="info">
              送り状番号を入力し、配送会社を選択して「更新」ボタンを押してください。<br/>
              ステータスが自動的に「出荷済み」に更新されます。
            </Alert>
            {success && <Alert variant="success">{success}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            
            {loading ? (
              <div className="text-center"><Spinner animation="border" /></div>
            ) : (
              <>
                <div className="d-flex justify-content-end mb-3">
                  <Button onClick={handleUpdate} disabled={updating}>
                    {updating ? <Spinner as="span" size="sm" /> : '入力した情報を更新'}
                  </Button>
                </div>
                
                <Tabs defaultActiveKey="processed" id="shipment-tabs" className="mb-3">
                  {/* 【変更点】タブのタイトルを更新 */}
                  <Tab eventKey="processed" title={`送り状NO.未発行 (${destinationsToProcess.length})`}>
                    {renderShipmentTable(destinationsToProcess)}
                  </Tab>
                  <Tab eventKey="shipped" title={`送り状NO.発行済 (${destinationsShipped.length})`}>
                    {/* 【コメントアウト】日付フィルターのUIをコメントアウト
                    <Row className="d-flex justify-content-start align-items-center my-3">
                        <Col md="auto">
                            <Form.Check 
                                type="switch"
                                id="today-filter-switch"
                                label="本日出荷予定分のみ表示"
                                checked={showOnlyToday}
                                onChange={(e) => setShowOnlyToday(e.target.checked)}
                            />
                        </Col>
                    </Row>
                    */}
                    {renderShipmentTable(destinationsShipped, true)}
                  </Tab>
                </Tabs>
              </>
            )}

            <hr className="my-4" />
            <Link to="/shipper-menu">出荷者メニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default ManualShipment;
